import React, { useState } from 'react';
import { CookieConsent } from '../../hooks/useCookieConsent';

interface CookieSettingsModalProps {
  consent: CookieConsent | null;
  onClose: () => void;
  onSave: (preferences: CookieConsent) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
}

const CookieCategoryToggle: React.FC<{ title: string; description: string; enabled: boolean; onToggle: () => void; disabled?: boolean }> = ({ title, description, enabled, onToggle, disabled = false }) => (
    <div className={`p-4 rounded-lg ${disabled ? 'bg-gray-200' : 'bg-gray-100'}`}>
        <div className="flex justify-between items-center">
            <h4 className="font-semibold text-gray-800">{title}</h4>
            <button
                onClick={onToggle}
                disabled={disabled}
                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${enabled ? 'bg-blue-600' : 'bg-gray-300'} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                aria-label={`Toggle ${title} cookies`}
            >
                <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
        </div>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
    </div>
);

const CookieSettingsModal: React.FC<CookieSettingsModalProps> = ({ consent, onClose, onSave, onAcceptAll, onRejectAll }) => {
  // Reflect whatever was actually saved before, not a hardcoded default --
  // otherwise reopening this modal after choosing "Necessary Only" would
  // show Performance/Functional as ON even though they were saved OFF.
  const [preferences, setPreferences] = useState<CookieConsent>({
    necessary: true,
    performance: consent?.performance ?? true,
    functional: consent?.functional ?? true,
  });

  const handleToggle = (category: keyof Omit<CookieConsent, 'necessary'>) => {
    setPreferences(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const handleSave = () => {
    onSave(preferences);
    onClose();
  };

  const handleAcceptAllAndClose = () => {
    onAcceptAll();
    onClose();
  };

  const handleRejectAllAndClose = () => {
    onRejectAll();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 border-b pb-4">
          <h2 className="text-xl font-bold text-gray-800">Cookie Preferences</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-3xl font-bold">&times;</button>
        </div>
        <p className="text-gray-600 mb-6">
          We use cookies to improve your experience on our site. You can customize your cookie settings below. Please note that strictly necessary cookies cannot be disabled.
        </p>

        <div className="space-y-4">
            <CookieCategoryToggle
                title="Strictly Necessary"
                description="These cookies are essential for the website to function and cannot be switched off. They are usually set in response to actions made by you, such as logging in or filling in forms."
                enabled={true}
                onToggle={() => {}}
                disabled={true}
            />
            <CookieCategoryToggle
                title="Performance & Analytics"
                description="These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. All information these cookies collect is aggregated and therefore anonymous."
                enabled={preferences.performance}
                onToggle={() => handleToggle('performance')}
            />
            <CookieCategoryToggle
                title="Functional"
                description="These cookies enable the website to provide enhanced functionality and personalization, such as remembering your accessibility settings."
                enabled={preferences.functional}
                onToggle={() => handleToggle('functional')}
            />
        </div>
        <div className="flex flex-wrap justify-end gap-3 mt-8">
            <button onClick={handleRejectAllAndClose} className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md font-semibold hover:bg-gray-300">
                Necessary Only
            </button>
            <button onClick={handleSave} className="px-6 py-2 bg-gray-600 text-white rounded-md font-semibold hover:bg-gray-700">
                Save Preferences
            </button>
            <button onClick={handleAcceptAllAndClose} className="px-6 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700">
                Accept All
            </button>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsModal;
